/**
 * The supply-chain reports (docs/24): the questions asked between the award
 * of a job and the last metre of cable used on it.
 */
import { defineReport, rc, rf } from './types.js'

/**
 * docs/24 §3.2 — which materials deserve to be chased first.
 *
 * Ranks materials by value, most expensive first, and suggests a class from
 * where each falls in the running total and from how long it takes to
 * arrive. The class actually assigned sits beside the suggestion, so a
 * classification nobody has revisited since the tender shows up.
 */
export const abcAnalysis = defineReport({
  key: 'abc_analysis', route: 'abc_analysis', group: 'Supply chain',
  label: 'ABC analysis',
  blurb: 'Materials ranked by value, with the ABC class value and lead time suggest against the one assigned.',
  filters: [
    { key: 'basis', label: 'Value from', control: 'select', options: [
      { value: 'ESTIMATE', label: 'The estimate — what the job will need' },
      { value: 'PURCHASE', label: 'Purchase orders — what was bought' },
    ], hint: 'Left empty, the estimate.' },
    rf.project(), rf.period('Ordered between', false), rf.category(),
  ],
  columns: [
    { key: 'rank', label: '#', type: 'text', width: 60, align: 'right' },
    rc.text('itemName', 'Material'),
    rc.text('categoryName', 'Category', 180),
    rc.qty('quantity', 'Quantity'),
    rc.money('value', 'Value'),
    rc.pct('share', 'Share'),
    rc.pct('cumulativeShare', 'Cumulative'),
    rc.text('valueClass', 'By value', 100),
    { key: 'leadTime', label: 'Lead time', type: 'text', width: 150 },
    rc.text('suggestedClass', 'Suggested', 110),
    rc.text('assignedClass', 'Assigned', 110),
    rc.text('verdict', 'Review', 220),
  ],
  caveat: 'S is never suggested: a special or customised material is a decision about its specification, not about its price.',
} as const)

/**
 * docs/24 §3.6 — every supplier on one sheet: what the records measure, and
 * what the last evaluation concluded.
 *
 * The measured columns are worked out afresh for the period asked; the grade
 * is the latest approved evaluation's. A supplier whose records have moved a
 * long way from their grade is one due for re-evaluation.
 */
export const vendorScorecard = defineReport({
  key: 'vendor_scorecard', route: 'vendor_scorecard', group: 'Supply chain',
  label: 'Vendor scorecard',
  blurb: 'Every supplier’s delivery, quality, pricing and compliance, beside their latest evaluation.',
  filters: [rf.period('Measured over', false), rf.supplier()],
  columns: [
    rc.text('supplierName', 'Supplier'),
    rc.text('approvalStatus', 'Status', 120),
    { key: 'orders', label: 'Orders', type: 'text', width: 90, align: 'right' },
    rc.money('orderValue', 'Ordered value'),
    rc.text('delivery', 'Delivery', 260),
    rc.text('quality', 'Quality', 240),
    rc.text('pricing', 'Pricing', 240),
    rc.text('compliance', 'Compliance', 240),
    { key: 'measuredScore', label: 'Measured', type: 'text', width: 100, align: 'right' },
    { key: 'latestScore', label: 'Evaluated', type: 'text', width: 100, align: 'right' },
    rc.text('grade', 'Grade', 80),
    rc.date('evaluatedOn', 'Evaluated on'),
  ],
  drill: { by: 'supplier', key: 'supplierId', docTypes: ['purchase_order', 'goods_receipt', 'vendor_evaluation'], label: 'Orders, receipts and evaluations' },
} as const)

/**
 * docs/24 §3.7 — every order still waiting for goods, and where they are.
 *
 * The expediter's list: what was promised by when, how long it takes, what
 * the supplier last said about it, and how late it is. Worked out from the
 * order, its receipts and the supplier's dispatch advices — nothing to keep
 * up to date by hand.
 */
export const poExpediting = defineReport({
  key: 'po_expediting', route: 'po_expediting', group: 'Supply chain',
  label: 'PO expediting',
  blurb: 'Open purchase orders: promised dates, dispatch status, LR numbers and how late each one is.',
  filters: [rf.project(), rf.supplier(), rf.period('Ordered between', false)],
  columns: [
    rc.doc(), rc.date('documentDate', 'Ordered'),
    rc.text('supplierName', 'Supplier', 200), rc.text('projectName', 'Project', 180),
    rc.text('abcClass', 'Class', 70),
    rc.date('promisedDate', 'Promised'),
    rc.text('leadTime', 'Lead time', 100),
    rc.pct('receivedPct', 'Received'),
    rc.text('dispatchStatus', 'Supplier says', 170),
    rc.text('lrNo', 'LR / AWB', 130),
    rc.date('expectedArrival', 'Expected'),
    { key: 'daysLate', label: 'Days late', type: 'text', width: 100, align: 'right' },
    rc.text('state', 'State', 200),
  ],
  drill: { by: 'document', key: 'id', label: 'The order and what came of it' },
} as const)

/**
 * docs/24 §3.9 — "Opening stock 100, receipt 50, issue 20": the stock register.
 *
 * Per material and location over a period: what was there at the start, what
 * came in — receipts, and returns, transfers and increases — what went out —
 * issues, and everything else — and what is left. Movements are the lots and
 * the ledger links off them, the same records Stock availability reads, so
 * the two agree on the closing figure — except that Stock availability also
 * holds back what draft documents have reserved, and this counts only what has
 * been submitted.
 */
export const stockLedger = defineReport({
  key: 'stock_ledger', route: 'stock_ledger', group: 'Inventory',
  label: 'Stock ledger',
  blurb: 'Opening, receipts, issues and closing stock per material and location, over a period.',
  filters: [
    rf.period('Period'), rf.warehouse(), rf.project(), rf.category(),
    { key: 'itemId', label: 'Material', control: 'lookup', lookup: { entity: 'item' } },
  ],
  columns: [
    rc.text('itemName', 'Material'), rc.text('locationName', 'Held at', 200),
    rc.qty('opening', 'Opening', true), rc.qty('receipts', 'Receipts (GRN)', true),
    rc.qty('otherIn', 'Returns and other in', true), rc.qty('issues', 'Issued', true),
    rc.qty('otherOut', 'Other out', true), rc.qty('closing', 'Closing', true),
  ],
  caveat: 'Approved and in-review documents count; drafts do not move stock until they are submitted.',
  drill: {
    by: 'item', key: 'itemId',
    docTypes: ['goods_receipt', 'site_inventory_record', 'material_transfer', 'material_consumption',
      'stock_adjustment', 'warehouse_transfer', 'delivery_challan', 'debit_note'],
    label: 'What moved this stock',
  },
} as const)

/**
 * docs/24 §3.9 — "Reconciliation": every quantity a material passes through on
 * one job, side by side.
 *
 * Estimated, requested, ordered, received and rejected, issued, then used,
 * wasted and returned — and what is still with the crew. Against it, what the
 * measured work (the JMR on work progress) says should have gone in; the
 * difference is what has to be explained.
 */
export const materialReconciliation = defineReport({
  key: 'material_reconciliation', route: 'material_reconciliation', group: 'Supply chain',
  label: 'Material reconciliation',
  blurb: 'Per material on a project: estimated, bought, received, issued, used, wasted, returned — against the JMR.',
  filters: [rf.project(true), rf.category()],
  columns: [
    rc.text('itemName', 'Material'), rc.text('unit', 'Unit', 80),
    rc.qty('estimated', 'Estimated'), rc.qty('requested', 'Requested'), rc.qty('ordered', 'Ordered'),
    rc.qty('received', 'Received'), rc.qty('rejected', 'Rejected'),
    rc.qty('issued', 'Issued'), rc.qty('used', 'Used'), rc.qty('wastage', 'Wastage'),
    rc.qty('returned', 'Returned'), rc.qty('atSite', 'Still at site'),
    rc.qty('theoretical', 'As per JMR'), rc.qty('variance', 'Over JMR'),
    rc.text('wastagePct', 'Wastage %', 100), rc.text('allowedPct', 'Allowed', 90),
    rc.text('verdict', 'Needs', 220),
  ],
  drill: {
    by: 'item', key: 'itemId',
    docTypes: ['material_request', 'purchase_order', 'goods_receipt', 'site_inventory_record',
      'material_transfer', 'material_consumption', 'work_progress'],
    label: 'Documents for this material',
  },
} as const)

/**
 * docs/24 §3.10 — "weekly or monthly one time should take stock audit".
 *
 * Every warehouse and site store, when it was last counted in full and when
 * each class was last cycle-counted, against how often the company's settings
 * say it should be — and how accurate the last count was.
 */
export const stockAuditStatus = defineReport({
  key: 'stock_audit_status', route: 'stock_audit_status', group: 'Inventory',
  label: 'Stock audit status',
  blurb: 'When each store was last counted, what is due, and how accurate the last count was.',
  filters: [rf.warehouse()],
  columns: [
    rc.text('locationName', 'Store'),
    rc.date('lastFull', 'Last full count'),
    rc.text('fullStatus', 'Full count', 190),
    rc.text('classA', 'Class A', 190),
    rc.text('classB', 'Class B', 190),
    rc.text('classC', 'Class C', 190),
    rc.text('lastAccuracy', 'Last count agreed', 140),
    rc.text('lastVariance', 'Last variance value', 150),
  ],
} as const)

/**
 * docs/24 §3.3 — MRP: what each job needs, when, and when to order it.
 *
 * One row per material and need-by date: what the estimate needs, what is
 * already on order and what the shelf can give, what is left to buy, and the
 * order-by date its lead time makes of the need-by date. Anything whose
 * order-by date has passed is late; anything due within the week is shown so
 * it can be bought before it is.
 */
export const materialRequirementPlan = defineReport({
  key: 'material_requirement_plan', route: 'mrp', group: 'Supply chain',
  label: 'Material requirement plan (MRP)',
  blurb: 'What each job needs and when, net of orders and stock — and the date each has to be ordered by.',
  filters: [
    rf.project(), rf.category(),
    { key: 'abcClass', label: 'Class', control: 'select', options: [
      { value: 'A', label: 'A' }, { value: 'B', label: 'B' }, { value: 'C', label: 'C' }, { value: 'S', label: 'S' },
    ] },
  ],
  columns: [
    rc.text('state', 'State', 170),
    rc.date('orderBy', 'Order by'),
    rc.text('projectName', 'Project', 180),
    rc.text('itemName', 'Material'),
    rc.text('abcClass', 'Class', 70),
    rc.date('needBy', 'Needed on site'),
    rc.text('needBySource', 'From', 130),
    rc.qty('estimated', 'Needed'), rc.qty('onOrder', 'Delivered, ordered or held'), rc.qty('fromStock', 'From free stock'),
    rc.qty('toOrder', 'To order'), rc.text('unit', 'Unit', 80),
    rc.text('leadTime', 'Lead time', 100),
  ],
  caveat: 'Order-by is the need-by date less the lead time and the buffer days in Material planning settings.',
  drill: { by: 'item', key: 'itemId', docTypes: ['material_request', 'purchase_requisition', 'purchase_order'], label: 'Requests and orders for this material' },
} as const)
